import {
  LocationFilterIntersectionFragment,
  ProximityPolicyFragment,
  ProximityPolicyRuleFragment,
  SimulationItemFilterIntersectionFragment,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { atom, selector, selectorFamily } from 'recoil';
import {
  viewerSelectedBayIdAtom,
  viewerSelectedLevel,
  viewerSelectedPlaneId,
} from '../layout/viewer/store/viewer.state';
import { simulationCurrent } from '../simulation/store/simulation.state';
import {
  simulationStorageTabKey,
  simulationWizardSelectedStepId,
} from '../simulation/store/simulation.wizard.state';
import { loadLocationsByRule } from './proximityPolicy.helper';
import { ProximityPolicySelectedIdentity } from './proximityPolicy.type';

const getKey = (postfix: string) =>
  `warebee-simulation-proximity-policy-${postfix}`;

const proximityPolicyIsVisible = selector<boolean>({
  key: getKey('is-visible'),
  get: ({ get }) => {
    const stepId = get(simulationWizardSelectedStepId);
    const tabId = get(simulationStorageTabKey);
    if (stepId !== 'policy-storage' || tabId !== 'tab-policy-proximity')
      return null;
  },
});

export const proximityPolicyDocument = atom<ProximityPolicyFragment>({
  key: getKey('document'),
  default: null,
});

export const proximityPolicySelectedIdentity =
  atom<ProximityPolicySelectedIdentity>({
    key: getKey('selected-identity'),
    default: null,
  });

export const proximityPolicyRule = selectorFamily<
  ProximityPolicyRuleFragment,
  string
>({
  key: getKey('rule-by-id'),
  get:
    ruleId =>
    ({ get }) => {
      const policy = get(proximityPolicyDocument);
      return _.find(policy?.rules, rule => rule.id === ruleId);
    },
  set:
    ruleId =>
    ({ get, set }, value: ProximityPolicyRuleFragment) => {
      const policy = get(proximityPolicyDocument);
      set(proximityPolicyDocument, {
        ...policy,
        rules: policy.rules.map(rule => (rule.id === ruleId ? value : rule)),
      });
    },
});

export const proximityPolicyLocationFilterIntersection = selectorFamily<
  LocationFilterIntersectionFragment,
  ProximityPolicySelectedIdentity
>({
  key: getKey('location-filter-intersection-by-id'),
  get:
    identity =>
    ({ get }) => {
      if (_.isNil(identity?.filterId) || _.isNil(identity?.ruleId)) return null;

      const rule = get(proximityPolicyRule(identity.ruleId));
      if (!rule) return null;

      const allFilters =
        identity.category === 'main'
          ? rule.mainCategory?.locationsMatch?.anyOf
          : rule.secondCategory?.locationsMatch?.anyOf;

      const filterIntersection = _.find(
        allFilters,
        fs => fs.id === identity.filterId,
      );
      return filterIntersection;
    },
  set:
    identity =>
    ({ get, set }, value: LocationFilterIntersectionFragment) => {
      if (_.isNil(identity?.filterId) || _.isNil(identity?.ruleId)) {
        throw new Error('Invalid parameters. Filter identity is invalid');
      }

      const rule = get(proximityPolicyRule(identity.ruleId));
      if (!rule) return null;

      set(proximityPolicyRule(identity.ruleId), {
        ...rule,
        mainCategory: {
          ...(rule.mainCategory ?? {}),
          locationsMatch: {
            ...(rule.mainCategory?.locationsMatch ?? {}),
            anyOf: _.map(rule.mainCategory?.locationsMatch?.anyOf, fs =>
              fs.id === identity.filterId ? value : fs,
            ),
          },
        },
        secondCategory: {
          ...(rule.secondCategory ?? {}),
          locationsMatch: {
            ...(rule.secondCategory?.locationsMatch ?? {}),
            anyOf: _.map(rule.secondCategory?.locationsMatch?.anyOf, fs =>
              fs.id === identity.filterId ? value : fs,
            ),
          },
        },
      });
    },
});

export const proximityPolicyProductFilterIntersection = selectorFamily<
  SimulationItemFilterIntersectionFragment,
  ProximityPolicySelectedIdentity
>({
  key: getKey('product-filter-intersection-by-id'),
  get:
    identity =>
    ({ get }) => {
      if (_.isNil(identity?.filterId) || _.isNil(identity?.ruleId)) return null;

      const rule = get(proximityPolicyRule(identity.ruleId));
      if (!rule) return null;

      const allFilters =
        identity.category === 'main'
          ? rule.mainCategory?.productsMatch?.anyOf
          : rule.secondCategory?.productsMatch?.anyOf;

      const filterIntersection = _.find(
        allFilters,
        fs => fs.id === identity.filterId,
      );
      return filterIntersection;
    },
  set:
    identity =>
    ({ get, set }, value: SimulationItemFilterIntersectionFragment) => {
      if (_.isNil(identity?.filterId) || _.isNil(identity?.ruleId)) {
        throw new Error('Invalid parameters. Filter identity is invalid');
      }

      const rule = get(proximityPolicyRule(identity.ruleId));
      if (!rule) return null;

      set(proximityPolicyRule(identity.ruleId), {
        ...rule,
        mainCategory: {
          ...(rule.mainCategory ?? {}),
          productsMatch: {
            ...(rule.mainCategory?.productsMatch ?? {}),
            anyOf: _.map(rule.mainCategory?.productsMatch?.anyOf, fs =>
              fs.id === identity.filterId ? value : fs,
            ),
          },
        },
        secondCategory: {
          ...(rule.secondCategory ?? {}),
          productsMatch: {
            ...(rule.secondCategory?.productsMatch ?? {}),
            anyOf: _.map(rule.secondCategory?.productsMatch?.anyOf, fs =>
              fs.id === identity.filterId ? value : fs,
            ),
          },
        },
      });
    },
});

// export const proximityPolicyCheckResult = atom<ProximityPolicyCheckResult>({
//   key: getKey('check-result'),
//   default: null,
// });

export const proximityPolicyLevelLocationByRule = selector<Set<string>>({
  key: getKey('locations-by-level-by-rule'),
  get: async ({ get }) => {
    const sim = get(simulationCurrent);
    const planeId = get(viewerSelectedPlaneId);
    const level = get(viewerSelectedLevel);
    const identity = get(proximityPolicySelectedIdentity);
    const layoutId = sim?.layout?.id;
    const isVisible = get(proximityPolicyIsVisible);
    if (
      !isVisible ||
      _.isNil(layoutId) ||
      _.isNil(identity?.ruleId) ||
      identity?.type !== 'locations' ||
      _.isNil(level)
    )
      return null;

    const rule = get(proximityPolicyRule(identity.ruleId));

    return loadLocationsByRule({
      layoutId,
      rule,
      category: identity.category,
      filter: {
        planeId: [planeId],
        level: [level],
      },
    });
  },
});

export const proximityPolicyBayLocationByRule = selector<Set<string>>({
  key: getKey('locations-per-bay-by-rule'),
  get: async ({ get }) => {
    const sim = get(simulationCurrent);
    const planeId = get(viewerSelectedPlaneId);
    const bay = get(viewerSelectedBayIdAtom);
    const identity = get(proximityPolicySelectedIdentity);
    const isVisible = get(proximityPolicyIsVisible);

    const layoutId = sim?.layout?.id;
    if (
      !isVisible ||
      _.isNil(layoutId) ||
      _.isNil(identity?.ruleId) ||
      _.isNil(bay) ||
      identity?.type !== 'locations'
    )
      return null;

    const rule = get(proximityPolicyRule(identity.ruleId));
    return loadLocationsByRule({
      layoutId,
      rule,
      category: identity.category,
      filter: {
        planeId: [planeId],
        bayId: [bay],
      },
    });
  },
});
